import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Box,
  CircularProgress,
  Typography,
  Alert,
  Stack,
} from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { Transaction, Status } from '../../../types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';
import { CopyableText } from '@/utils/copy';
import { formatApiDateTime } from '@/utils/dateTime';

const getStatusColor = (status: Status) => {
  switch (status) {
    case Status.SUCCESS:
      return 'success';
    case Status.DECLINED:
      return 'error';
    case Status.ACCEPTED:
      return 'warning';
    case Status.APPEAL:
      return 'info';
    case Status.NOT_FOUND:
      return 'default';
    default:
      return 'default';
  }
};

const isSandboxTransaction = (transaction: Transaction) => {
  try {
    return JSON.parse(transaction.extra_info || '{}').sandbox === true;
  } catch {
    return false;
  }
};

const getTransactionCurrency = (transaction: Transaction): string => {
  const currency = transaction.payment_system?.currency;
  return transaction.currency_code
    || (typeof currency === 'string' ? currency : currency?.iso_code || currency?.code)
    || 'Валюта неизвестна';
};

interface TransactionsTableProps {
  transactions: Transaction[];
  merchants: any[];
  traders: any[];
  loading: boolean;
  error: string | null;
  onViewDetails: (transaction: Transaction) => void;
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  transactions,
  merchants,
  traders,
  loading,
  error,
  onViewDetails,
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canViewDetails = useMemo(() => canPerformAction(currentUser, ActionKey.TRANSACTIONS_VIEW_DETAILS), [currentUser]);

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Мерчант</TableCell>
            <TableCell>Трейдер</TableCell>
            <TableCell>Направление</TableCell>
            <TableCell>Сумма</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Дата создания</TableCell>
            <TableCell>Действия</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                  <CircularProgress />
                  <Typography variant="body2" color="textSecondary">
                    Загрузка данных...
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ width: '100%', maxWidth: 500 }}>
                  {error}
                </Alert>
              </TableCell>
            </TableRow>
          ) : transactions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  Нет данных для отображения
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            transactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>
                  <CopyableText
                    value={String(transaction.id)}
                    copiedMessage="ID транзакции скопирован"
                  />
                </TableCell>
                <TableCell>
                  {merchants.find((m) => m.id === transaction.merchant_id)?.name || '-'}
                </TableCell>
                <TableCell>
                  {traders.find((t) => t.id === transaction.trader_id)?.name || '-'}
                </TableCell>
                <TableCell>{transaction.direction}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {Number(transaction.amount).toFixed(2)} {getTransactionCurrency(transaction)}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={transaction.status}
                      color={getStatusColor(transaction.status)}
                      size="small"
                      sx={{ minWidth: '100px', justifyContent: 'center' }}
                    />
                    {isSandboxTransaction(transaction) && <Chip label="SANDBOX" color="warning" size="small" />}
                  </Stack>
                </TableCell>
                <TableCell>
                  {formatApiDateTime(transaction.date_create)}
                </TableCell>
                <TableCell>
                  <Tooltip title={canViewDetails ? 'Просмотр деталей' : 'Недостаточно прав'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onViewDetails(transaction)}
                        disabled={!canViewDetails}
                      >
                        <InfoIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TransactionsTable;
